import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { qcService } from '@/services/qcService';
import { getAllInspectionChecklists, type InspectionChecklistResponse } from '@/api/inspectionchecklist.api';
import { getAllQualityParameters, type QualityParameterResponse } from '@/api/qualityparameter.api';
import { useWorkCenters } from '@/hooks/useWorkCenters';

export interface QCParameter {
  id: string;
  name: string;
  type?: 'NUMERIC' | 'PASS_FAIL';
  specification: string;
  nominal?: string;
  tolerance?: string;
  mandatory?: boolean;
  observedValue?: string;
  status: 'PASSED' | 'FAILED' | 'PENDING' | 'IN_PROGRESS';
  inspectedBy?: string;
  inspectedAt?: string;
  remarks?: string;
}

export interface QCStageCheck {
  stageNumber: number;
  stageName: string;
  workOrderNumber: string;
  workCenterName: string;
  machineName?: string;
  gateRequirement?: string;
  isMandatoryGate: boolean;
  status: 'COMPLETED' | 'IN_PROGRESS' | 'QC_PENDING' | 'QC_FAILED' | 'PENDING';
  inspectorName?: string;
  completedAt?: string;
  inspectionNumber?: string;
  checklistId?: string;
  checklistName?: string;
  parameters: QCParameter[];
}

export interface QualityControlTabProps {
  order: any;
  workOrders: any[];
  isCompletedOrder: boolean;
  onOpenQcInspectionModal?: (wo: any, stageType?: string) => void;
  onQuickPassQc?: (wo: any) => Promise<void>;
  onQuickFailQc?: (wo: any) => Promise<void>;
  actionLoading?: boolean;
  onConfirmOrder?: () => void;
}

export default function QualityControlTab({
  order,
  workOrders,
  isCompletedOrder,
  onOpenQcInspectionModal,
  onQuickPassQc,
  onQuickFailQc,
  actionLoading = false,
  onConfirmOrder,
}: QualityControlTabProps) {
  // Work center shown as "WC-001 · Assembly Line"
  const workCenterLookup = useWorkCenters();
  const toast = useToast();

  // Filters & Search
  const [filterStage, setFilterStage] = useState<string>('ALL');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Expand / collapse states
  const [expandedStages, setExpandedStages] = useState<Record<number, boolean>>({
    1: true,
    2: true,
    3: true,
    4: true,
    5: true,
  });

  // Live Backend QC Data
  const [backendInspections, setBackendInspections] = useState<any[]>([]);
  const [inspectionDetailsMap, setInspectionDetailsMap] = useState<Record<string, any>>({});
  const [backendNcrs, setBackendNcrs] = useState<any[]>([]);
  const [isLoadingQc, setIsLoadingQc] = useState<boolean>(false);

  // Record Inspection Modal State
  const [inspectModalStage, setInspectModalStage] = useState<QCStageCheck | null>(null);
  const [modalParamForm, setModalParamForm] = useState<
    Record<string, { observedValue: string; status: 'PASS' | 'FAIL'; remarks: string }>
  >({});
  const [modalInspectorName, setModalInspectorName] = useState<string>('');
  const [modalSubmitting, setModalSubmitting] = useState<boolean>(false);

  // Print Quality Card Modal State
  const [printModalOpen, setPrintModalOpen] = useState<boolean>(false);

  const poNumber = order?.po_number || order?.order_number || '';
  const productName = order?.product_name || order?.item_name || '';
  // Only a real batch: none is assigned until the order's output is booked (no made-up number)
  const batchNumber = order?.batch_number || order?.output_batch_number || 'Not assigned yet';

  // Fetch Live Quality Data from Backend API
  const fetchQualityData = useCallback(async () => {
    if (!poNumber || order?.status === 'DRAFT' || order?.status === 'CANCELLED') {
      setBackendInspections([]);
      setBackendNcrs([]);
      return;
    }
    setIsLoadingQc(true);
    try {
      // By order id: also finds stage inspections, which carry the work order's number
      const inspections = order?.id
        ? await qcService.getInspections({ productionOrderId: order.id })
        : await qcService.getInspections({ sourceFilter: poNumber });
      setBackendInspections(inspections || []);

      if (Array.isArray(inspections) && inspections.length > 0) {
        const detailsPromises = inspections.map(async (insp) => {
          try {
            const detail = await qcService.getInspectionById(insp.id);
            return { id: insp.id, detail };
          } catch {
            return null;
          }
        });
        const detailsResults = await Promise.all(detailsPromises);
        const map: Record<string, any> = {};
        detailsResults.forEach((item) => {
          if (item && item.detail) {
            map[item.id] = item.detail;
          }
        });
        setInspectionDetailsMap(map);
      }

      try {
        const ncrs = await qcService.getNCRs({ search: poNumber });
        setBackendNcrs(ncrs || []);
      } catch {
        // quiet fallback for NCRs
      }
    } catch (err) {
      console.error('Error fetching quality data:', err);
    } finally {
      setIsLoadingQc(false);
    }
  }, [poNumber, order?.id]);

  useEffect(() => {
    fetchQualityData();
  }, [fetchQualityData]);

  // Master Checklists & Quality Parameters from backend as backup resolution
  const [masterChecklists, setMasterChecklists] = useState<InspectionChecklistResponse[]>([]);
  const [masterQualityParams, setMasterQualityParams] = useState<QualityParameterResponse[]>([]);

  useEffect(() => {
    let mounted = true;
    getAllInspectionChecklists()
      .then((res) => {
        if (mounted && res?.data) setMasterChecklists(res.data);
      })
      .catch(() => {});
    getAllQualityParameters()
      .then((res) => {
        if (mounted && res?.data) setMasterQualityParams(res.data);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // Dynamic Synthesis of Stage QC List based strictly on routing QC assignments
  const stageQcList = useMemo<QCStageCheck[]>(() => {
    if (order?.status === 'DRAFT' || order?.status === 'CANCELLED') {
      return [];
    }

    const isOrderDone = isCompletedOrder || order?.status === 'COMPLETED';
    const routingStages: any[] = Array.isArray(order?.routing_stages)
      ? order.routing_stages
      : Array.isArray(order?.routing?.stages)
      ? order.routing.stages
      : Array.isArray(order?.stages)
      ? order.stages
      : [];

    if (Array.isArray(workOrders) && workOrders.length > 0) {
      return workOrders.map((wo: any, idx: number) => {
        const seq = Number(wo.stage_number) || (idx + 1);
        const stageName = wo.stage_name || `Stage ${seq}`;

        const matchedRoutingStage = routingStages.find((st: any) => {
          const stSeq = Number(st.sequence || st.stageNumber || st.sequence_no);
          return (
            stSeq === seq ||
            (st.stageName && stageName && st.stageName.toLowerCase().trim() === stageName.toLowerCase().trim()) ||
            (st.operationName && stageName && st.operationName.toLowerCase().trim() === stageName.toLowerCase().trim())
          );
        });

        const isQcRequired = matchedRoutingStage
          ? Boolean(matchedRoutingStage.qcRequired ?? matchedRoutingStage.qc_enabled ?? matchedRoutingStage.requires_qc)
          : Boolean(wo.qc_required);

        const checklistId =
          matchedRoutingStage?.qcChecklistId ||
          matchedRoutingStage?.checklist_id ||
          wo.qc_checklist_id ||
          null;

        const checklistName =
          matchedRoutingStage?.qcChecklistName ||
          matchedRoutingStage?.checklist_name ||
          wo.qc_checklist_name ||
          null;

        const matchedInspection = backendInspections.find((insp) => {
          const notes = (insp.notes || '').toLowerCase();
          const item = (insp.itemName || '').toLowerCase();
          const woNum = (wo.wo_number || '').toLowerCase();
          const stgName = stageName.toLowerCase();
          return (
            (wo.id && String(insp.sourceId || '') === String(wo.id)) ||
            (woNum && String(insp.sourceNumber || '').toLowerCase() === woNum) ||
            (woNum && notes.includes(woNum)) ||
            (woNum && item.includes(woNum)) ||
            (insp.sourceNumber === poNumber && item.includes(stgName)) ||
            (insp.sourceNumber === poNumber && notes.includes(stgName))
          );
        });

        const isOrderDraft = order?.status === 'DRAFT';
        const isWoCompleted = !isOrderDraft && (wo.status === 'COMPLETED' || isOrderDone);
        const isWoQcPending = !isOrderDraft && (wo.status === 'QC_PENDING' || wo.status === 'COMPLETED_QC_PENDING');
        const isWoQcFailed = !isOrderDraft && wo.status === 'QC_FAILED';
        const isWoInProgress = !isOrderDraft && wo.status === 'IN_PROGRESS';

        const stageStatus: QCStageCheck['status'] = isWoCompleted
          ? 'COMPLETED'
          : isWoQcPending
          ? 'QC_PENDING'
          : isWoQcFailed
          ? 'QC_FAILED'
          : isWoInProgress
          ? 'IN_PROGRESS'
          : 'PENDING';

        let inspectorName = wo.operator_name || (matchedInspection?.inspectorName) || (isQcRequired ? 'QC Inspector' : undefined);
        let completedAt = isWoCompleted ? (wo.actual_end_time || matchedInspection?.completedDate || 'Verified') : undefined;

        let baseParameters: QCParameter[] = [];

        if (isQcRequired) {
          const preResolved = matchedRoutingStage?.qcParameters || wo.qc_parameters;
          if (Array.isArray(preResolved) && preResolved.length > 0) {
            baseParameters = preResolved.map((p: any, pIdx: number) => {
              const pType = (p.type || 'PASS_FAIL') as 'NUMERIC' | 'PASS_FAIL';
              let spec = p.specification;
              if (!spec) {
                if (pType === 'NUMERIC' && p.minValue != null && p.maxValue != null) {
                  spec = `${p.minValue} - ${p.maxValue} ${p.unit || ''}`.trim();
                } else if (pType === 'NUMERIC' && p.minValue != null) {
                  spec = `Min ${p.minValue} ${p.unit || ''}`.trim();
                } else if (pType === 'NUMERIC' && p.maxValue != null) {
                  spec = `Max ${p.maxValue} ${p.unit || ''}`.trim();
                } else {
                  spec = 'Standard Conformance (Pass / Fail)';
                }
              }
              return {
                id: p.id || `qp-${seq}-${pIdx}`,
                name: p.name,
                type: pType,
                specification: spec,
                nominal: p.nominal,
                tolerance: p.tolerance,
                mandatory: true,
                status: isWoCompleted ? ('PASSED' as const) : ('PENDING' as const),
                observedValue: isWoCompleted ? (p.nominal || 'Passed within spec') : undefined,
              };
            });
          } else if (checklistId) {
            const cleanId = String(checklistId).replace('checklist:', '').replace('param:', '');
            const targetChecklist = masterChecklists.find(
              (c) => c.id === cleanId || `checklist:${c.id}` === checklistId || c.id === checklistId
            );

            if (targetChecklist && Array.isArray(targetChecklist.parameter_ids) && targetChecklist.parameter_ids.length > 0) {
              baseParameters = targetChecklist.parameter_ids.map((pid, pIdx) => {
                const qp = masterQualityParams.find((p) => p.id === pid);
                const pName = qp?.name || `Parameter ${pIdx + 1}`;
                const pType = (qp?.type || 'PASS_FAIL') as 'NUMERIC' | 'PASS_FAIL';
                let spec = 'Standard Conformance (Pass / Fail)';
                let nominal: string | undefined;
                let tolerance: string | undefined;

                if (pType === 'NUMERIC') {
                  if (qp?.min_value != null && qp?.max_value != null) {
                    spec = `${qp.min_value} - ${qp.max_value} ${qp.unit || ''}`.trim();
                    const minN = Number(qp.min_value);
                    const maxN = Number(qp.max_value);
                    nominal = `${((minN + maxN) / 2).toFixed(2)} ${qp.unit || ''}`.trim();
                    tolerance = `±${((maxN - minN) / 2).toFixed(2)} ${qp.unit || ''}`.trim();
                  } else if (qp?.min_value != null) {
                    spec = `Min ${qp.min_value} ${qp.unit || ''}`.trim();
                  } else if (qp?.max_value != null) {
                    spec = `Max ${qp.max_value} ${qp.unit || ''}`.trim();
                  }
                }

                return {
                  id: qp?.id || `qp-${seq}-${pIdx}`,
                  name: pName,
                  type: pType,
                  specification: spec,
                  nominal,
                  tolerance,
                  mandatory: true,
                  status: isWoCompleted ? ('PASSED' as const) : ('PENDING' as const),
                  observedValue: isWoCompleted ? (nominal || 'Passed within spec') : undefined,
                };
              });
            } else {
              const directQp = masterQualityParams.find((p) => p.id === cleanId || `param:${p.id}` === checklistId);
              if (directQp) {
                const pType = (directQp.type || 'PASS_FAIL') as 'NUMERIC' | 'PASS_FAIL';
                baseParameters = [{
                  id: directQp.id,
                  name: directQp.name,
                  type: pType,
                  specification: pType === 'NUMERIC' ? `${directQp.min_value} - ${directQp.max_value} ${directQp.unit || ''}`.trim() : 'Standard Conformance (Pass / Fail)',
                  mandatory: true,
                  status: isWoCompleted ? ('PASSED' as const) : ('PENDING' as const),
                  observedValue: isWoCompleted ? 'Passed within spec' : undefined,
                }];
              }
            }
          }
        }

        let parameters: QCParameter[] = [];

        if (matchedInspection && inspectionDetailsMap[matchedInspection.id]?.results?.length > 0) {
          const rawResults = inspectionDetailsMap[matchedInspection.id].results;

          if (baseParameters.length > 0) {
            parameters = baseParameters.map((bp) => {
              const matchedRes = rawResults.find(
                (r: any) =>
                  r.parameterId === bp.id ||
                  (r.parameterName && bp.name && r.parameterName.toLowerCase().trim() === bp.name.toLowerCase().trim())
              );
              if (matchedRes) {
                const pStatus = matchedRes.status === 'PASS' ? 'PASSED' : matchedRes.status === 'FAIL' ? 'FAILED' : 'PENDING';
                return {
                  ...bp,
                  observedValue: matchedRes.observedValue || (pStatus === 'PASSED' ? (bp.nominal || 'Passed') : undefined),
                  status: pStatus,
                  remarks: matchedRes.notes || (pStatus === 'PASSED' ? 'Conforms to specification' : undefined),
                };
              }
              return bp;
            });
          } else {
            parameters = rawResults.map((r: any, rIdx: number) => {
              const pName = r.parameterName || `Check ${rIdx + 1}`;
              const pType = (r.parameterType || 'PASS_FAIL') as 'NUMERIC' | 'PASS_FAIL';
              let spec = '';
              if (r.minValue != null && r.maxValue != null) {
                spec = `${r.minValue} - ${r.maxValue} ${r.unit || ''}`.trim();
              } else if (r.minValue != null) {
                spec = `Min ${r.minValue} ${r.unit || ''}`.trim();
              } else if (r.maxValue != null) {
                spec = `Max ${r.maxValue} ${r.unit || ''}`.trim();
              } else {
                spec = 'Standard Conformance (Pass / Fail)';
              }

              const pStatus = r.status === 'PASS' ? 'PASSED' : r.status === 'FAIL' ? 'FAILED' : 'PENDING';

              return {
                id: r.id || `param-${matchedInspection.id}-${rIdx}`,
                name: pName,
                type: pType,
                specification: spec,
                nominal: r.minValue != null && r.maxValue != null ? `${((Number(r.minValue) + Number(r.maxValue)) / 2).toFixed(2)} ${r.unit || ''}`.trim() : undefined,
                mandatory: true,
                observedValue: r.observedValue || (pStatus === 'PASSED' ? 'Passed within spec' : undefined),
                status: pStatus,
                remarks: r.notes || (pStatus === 'PASSED' ? 'Conforms to specification' : undefined),
              };
            });
          }

          if (matchedInspection.inspectorName) inspectorName = matchedInspection.inspectorName;
          if (matchedInspection.completedDate) completedAt = matchedInspection.completedDate;
        } else {
          parameters = baseParameters;
        }

        return {
          stageNumber: seq,
          stageName,
          workOrderNumber: wo.wo_number || `WO-${String(seq).padStart(3, '0')}`,
          workCenterName: wo.work_center_name || '—',
          machineName: wo.machine_name || undefined,
          isMandatoryGate: isQcRequired,
          status: stageStatus,
          inspectorName,
          completedAt,
          inspectionNumber: matchedInspection?.inspectionNumber,
          checklistId: checklistId || undefined,
          checklistName: checklistName || undefined,
          parameters,
        };
      });
    }

    if (routingStages.length > 0) {
      return routingStages.map((st: any, idx: number) => {
        const seq = Number(st.sequence || st.stageNumber || idx + 1);
        const stageName = st.operationName || st.stageName || `Stage ${seq}`;
        const isQcRequired = Boolean(st.qcRequired ?? st.qc_enabled ?? st.requires_qc);
        const params: QCParameter[] = Array.isArray(st.qcParameters)
          ? st.qcParameters.map((p: any, pIdx: number) => ({
              id: p.id || `qp-${seq}-${pIdx}`,
              name: p.name,
              type: p.type || 'PASS_FAIL',
              specification: p.specification || 'Pass / Fail',
              nominal: p.nominal,
              tolerance: p.tolerance,
              mandatory: true,
              status: isOrderDone ? ('PASSED' as const) : ('PENDING' as const),
              observedValue: isOrderDone ? (p.nominal || 'Passed within spec') : undefined,
            }))
          : [];

        return {
          stageNumber: seq,
          stageName,
          workOrderNumber: `WO-${poNumber || '001'}-${String(seq).padStart(2, '0')}`,
          workCenterName: st.workCenterName || '—',
          machineName: st.machineName || undefined,
          isMandatoryGate: isQcRequired,
          status: isOrderDone ? 'COMPLETED' : 'PENDING',
          inspectorName: isQcRequired ? 'QC Inspector' : undefined,
          completedAt: isOrderDone ? 'Verified' : undefined,
          checklistId: st.qcChecklistId || undefined,
          checklistName: st.qcChecklistName || undefined,
          parameters: params,
        };
      });
    }

    return [];
  }, [
    workOrders,
    isCompletedOrder,
    order,
    backendInspections,
    inspectionDetailsMap,
    masterChecklists,
    masterQualityParams,
    poNumber,
  ]);

  // Filtering Logic
  const filteredStages = useMemo(() => {
    return stageQcList
      .filter((stg) => {
        if (filterStage !== 'ALL' && String(stg.stageNumber) !== filterStage) {
          return false;
        }
        if (filterStatus !== 'ALL') {
          if (filterStatus === 'PASSED' && stg.status !== 'COMPLETED') return false;
          if (filterStatus === 'PENDING' && (stg.status === 'COMPLETED' || stg.status === 'QC_FAILED')) return false;
          if (filterStatus === 'FAILED' && stg.status !== 'QC_FAILED') return false;
        }
        return true;
      })
      .map((stg) => {
        const matchingParams = stg.parameters.filter((p) => {
          if (searchQuery) {
            const q = searchQuery.toLowerCase();
            const matches =
              p.name.toLowerCase().includes(q) ||
              p.specification.toLowerCase().includes(q) ||
              stg.stageName.toLowerCase().includes(q) ||
              stg.workOrderNumber.toLowerCase().includes(q);
            if (!matches) return false;
          }
          return true;
        });

        return {
          ...stg,
          filteredParameters: matchingParams,
        };
      });
  }, [stageQcList, filterStage, filterStatus, searchQuery]);

  const toggleStage = (stageNum: number) => {
    setExpandedStages((prev) => ({
      ...prev,
      [stageNum]: !prev[stageNum],
    }));
  };

  const expandAll = () => {
    const next: Record<number, boolean> = {};
    stageQcList.forEach((s) => {
      next[s.stageNumber] = true;
    });
    setExpandedStages(next);
  };

  const collapseAll = () => {
    setExpandedStages({});
  };

  // Open Inspection Page or fallback modal
  const handleOpenInspectionModal = (stage: QCStageCheck) => {
    if (order?.status === 'ON_HOLD') {
      toast.error('Cannot perform QC inspection while order is ON HOLD. Resume order first.');
      return;
    }
    if (onOpenQcInspectionModal) {
      const matchingWo = workOrders?.find(
        (w: any) =>
          Number(w.stage_number) === stage.stageNumber ||
          w.wo_number === stage.workOrderNumber
      );
      onOpenQcInspectionModal(matchingWo, stage.stageNumber === 3 ? 'WO3' : 'WO1');
      return;
    }
    setInspectModalStage(stage);
    const initialForm: Record<string, { observedValue: string; status: 'PASS' | 'FAIL'; remarks: string }> = {};
    stage.parameters.forEach((p) => {
      initialForm[p.id] = {
        observedValue: p.observedValue || p.nominal || 'Passed',
        status: p.status === 'FAILED' ? 'FAIL' : 'PASS',
        remarks: p.remarks || 'Verified during stage sign-off',
      };
    });
    setModalParamForm(initialForm);
    setModalInspectorName(stage.inspectorName || 'QC Inspector');
  };

  // Submit Inspection Results from In-Tab Modal
  const handleSubmitInspection = async (overallDecision: 'PASS' | 'FAIL') => {
    if (!inspectModalStage) return;
    if (order?.status === 'ON_HOLD') {
      toast.error('Cannot submit inspection while order is ON HOLD. Resume order first.');
      return;
    }
    setModalSubmitting(true);
    try {
      const matchingWo = workOrders?.find(
        (w: any) =>
          Number(w.stage_number) === inspectModalStage.stageNumber ||
          w.wo_number === inspectModalStage.workOrderNumber
      );

      const matchedInsp = backendInspections.find((insp) => {
        const notes = (insp.notes || '').toLowerCase();
        const item = (insp.itemName || '').toLowerCase();
        const woNum = (inspectModalStage.workOrderNumber || '').toLowerCase();
        const stgName = inspectModalStage.stageName.toLowerCase();
        return (
          (woNum && String(insp.sourceNumber || '').toLowerCase() === woNum) ||
          (woNum && notes.includes(woNum)) ||
          (woNum && item.includes(woNum)) ||
          (insp.sourceNumber === poNumber && item.includes(stgName)) ||
          (insp.sourceNumber === poNumber && notes.includes(stgName))
        );
      });

      if (matchedInsp?.id) {
        try {
          const formattedResults = inspectModalStage.parameters.length > 0
            ? inspectModalStage.parameters.map((p) => {
                const currentVal = modalParamForm[p.id];
                return {
                  parameterName: p.name,
                  parameterType: (p.type === 'NUMERIC' ? 'NUMERIC' : 'PASS_FAIL') as 'NUMERIC' | 'PASS_FAIL',
                  observedValue: currentVal?.observedValue || (currentVal?.status === 'PASS' ? 'Within specification' : 'Non-compliant'),
                  status: (currentVal?.status === 'PASS' ? 'PASS' : 'FAIL') as 'PASS' | 'FAIL',
                  notes: currentVal?.remarks || undefined,
                };
              })
            : [{
                parameterName: 'Overall Stage Inspection',
                parameterType: 'PASS_FAIL' as const,
                observedValue: overallDecision === 'PASS' ? 'PASS' : 'FAIL',
                status: overallDecision === 'PASS' ? 'PASS' : 'FAIL',
              }];

          await qcService.submitInspectionResults(matchedInsp.id, {
            results: formattedResults,
            overallStatus: overallDecision === 'PASS' ? 'PASSED' : 'FAILED',
          });
        } catch (subErr) {
          console.warn('Could not submit QC parameter results to inspection record:', subErr);
        }
      }

      if (overallDecision === 'PASS') {
        if (onQuickPassQc && matchingWo) {
          await onQuickPassQc(matchingWo);
        } else {
          toast.success(`Stage ${inspectModalStage.stageName} Passed Quality Inspection!`);
        }
      } else {
        if (onQuickFailQc && matchingWo) {
          await onQuickFailQc(matchingWo);
        } else {
          toast.error(`Stage ${inspectModalStage.stageName} Marked as Failed / NCR Logged!`);
        }
      }

      setInspectModalStage(null);
      await fetchQualityData();
    } catch (err: any) {
      toast.error(err?.message || 'Error submitting inspection results');
    } finally {
      setModalSubmitting(false);
    }
  };

  if (order?.status === 'DRAFT') {
    return (
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-12 text-center space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto text-amber-600 shadow-2xs">
          <i className="ri-shield-line text-3xl" />
        </div>
        <div className="max-w-md mx-auto space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
            <i className="ri-draft-line text-xs" />
            <span>Order in DRAFT Status</span>
          </div>
          <h3 className="text-base font-bold text-slate-800">Quality Control Inactive</h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            Quality control gates, testing parameters, and stage inspection checklists will be generated automatically once this production order is CONFIRMED.
          </p>
        </div>
        {onConfirmOrder && (
          <button
            type="button"
            onClick={onConfirmOrder}
            disabled={actionLoading}
            className="h-9 px-4 rounded-xl bg-[#4f46e5] hover:bg-indigo-700 text-white text-xs font-semibold shadow-xs inline-flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
          >
            <i className="ri-checkbox-circle-line text-sm" />
            Confirm Order to Activate Quality Control
          </button>
        )}
      </div>
    );
  }

  if (order?.status === 'CANCELLED') {
    return (
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-12 text-center space-y-4">
        <div className="max-w-md mx-auto space-y-2">
          
          <h3 className="text-base font-bold text-slate-800">Quality Control Inactive</h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            This production order has been cancelled. No quality control inspections or testing can be performed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── ON HOLD Notice ── */}
      {order?.status === 'ON_HOLD' && (
        <div className="bg-amber-50/90 border border-amber-200/90 rounded-2xl p-4 shadow-xs flex items-center justify-between text-xs text-amber-900">
          <div className="flex items-center gap-2.5">
            <i className="ri-pause-circle-fill text-amber-600 text-lg shrink-0" />
            <div>
              <strong className="text-amber-900">Quality Control Paused (Order ON HOLD)</strong>
              <p className="text-amber-700 mt-0.5">
                Stage inspections, parameter tests, and quality sign-offs are locked until the production order is resumed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── 1. Filters & Search Toolbar ── */}
      <div className="bg-white rounded-2xl border border-slate-200/90 p-3.5 sm:p-4 shadow-xs flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 flex-1 min-w-[240px]">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input
              type="text"
              placeholder="Search parameter, checkpoint, or stage..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-9 pl-9 pr-8 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:bg-white transition-all"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <i className="ri-close-line text-xs" />
              </button>
            )}
          </div>
        </div>

        {/* Dropdown Filters & Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={filterStage}
            onChange={(e) => setFilterStage(e.target.value)}
            className="h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700 font-medium focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer"
          >
            <option value="ALL">All Stages ({stageQcList.length})</option>
            {stageQcList.map((s) => (
              <option key={s.stageNumber} value={String(s.stageNumber)}>
                Stage {s.stageNumber}: {s.stageName}
              </option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700 font-medium focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer"
          >
            <option value="ALL">All Statuses</option>
            <option value="PASSED">Passed / Cleared</option>
            <option value="PENDING">Pending QC</option>
            <option value="FAILED">Failed / NCR</option>
          </select>

          {(filterStage !== 'ALL' || filterStatus !== 'ALL' || searchQuery) && (
            <button
              type="button"
              onClick={() => {
                setFilterStage('ALL');
                setFilterStatus('ALL');
                setSearchQuery('');
              }}
              className="h-9 px-3 text-xs text-rose-600 hover:bg-rose-50 rounded-lg font-semibold transition-colors cursor-pointer"
            >
              Reset
            </button>
          )}

          <div className="h-5 w-px bg-slate-200 mx-1 hidden sm:block" />

          <button
            type="button"
            onClick={expandAll}
            className="h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 text-xs font-medium transition-colors cursor-pointer"
            title="Expand all stages"
          >
            Expand All
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className="h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 text-xs font-medium transition-colors cursor-pointer"
            title="Collapse all stages"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* DRAFT Notice Banner */}
      {order?.status === 'DRAFT' && (
        <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2.5">
            <i className="ri-information-line text-amber-600 text-base" />
            <span>
              <strong>DRAFT Order:</strong> Quality inspections and stage gate sign-offs are disabled until this production order is confirmed.
            </span>
          </div>
        </div>
      )}

      {/* ── 2. Stage-by-Stage QC Cards ── */}
      <div className="space-y-4">
        {filteredStages.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200/90 p-12 text-center text-slate-400 shadow-xs">
            <i className="ri-filter-3-line text-3xl text-slate-300 block mb-2" />
            <p className="text-sm font-semibold text-slate-700">No QC Checkpoints Match Filters</p>
            <p className="text-xs text-slate-400 mt-1">Try clearing search terms or resetting dropdown filters.</p>
          </div>
        ) : (
          filteredStages.map((stage) => {
            const isExpanded = !!expandedStages[stage.stageNumber];
            const passedCount = stage.parameters.filter((p) => p.status === 'PASSED').length;
            const totalCount = stage.parameters.length;
            const isStageCompleted = stage.status === 'COMPLETED';
            const isStagePendingQc = stage.status === 'QC_PENDING';
            const isStageFailed = stage.status === 'QC_FAILED';
            const isStageInProgress = stage.status === 'IN_PROGRESS';

            const matchingWo =
              workOrders?.find(
                (w: any) =>
                  Number(w.stage_number) === stage.stageNumber ||
                  w.wo_number === stage.workOrderNumber
              ) || {
                id: `wo-${stage.stageNumber}`,
                wo_number: stage.workOrderNumber,
                stage_name: stage.stageName,
                stage_number: stage.stageNumber,
                status: stage.status,
              };

            return (
              <div
                key={stage.stageNumber}
                className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden transition-all"
              >
                {/* ── Stage Card Header ── */}
                <div
                  onClick={() => toggleStage(stage.stageNumber)}
                  className="p-4 sm:p-5 flex items-center justify-between gap-3 cursor-pointer select-none transition-colors hover:bg-slate-50/70 bg-white"
                >
                  <div className="flex items-center gap-3.5 flex-1 min-w-0">
                    {/* Sequence Badge */}
                    <div
                      className={`w-8 h-8 rounded-lg font-bold text-xs flex items-center justify-center shrink-0 border ${
                        isStageCompleted
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : isStageFailed
                          ? 'bg-rose-50 text-rose-700 border-rose-200'
                          : isStagePendingQc
                          ? 'bg-purple-50 text-purple-700 border-purple-200 animate-pulse'
                          : 'bg-slate-100 text-slate-700 border-slate-200'
                      }`}
                    >
                      {isStageCompleted ? (
                        <i className="ri-check-line text-sm font-bold" />
                      ) : isStageFailed ? (
                        <i className="ri-close-line text-sm font-bold" />
                      ) : isStagePendingQc ? (
                        <i className="ri-microscope-line text-sm" />
                      ) : (
                        stage.stageNumber
                      )}
                    </div>

                    {/* Stage Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-800 text-sm sm:text-base">
                          {stage.stageName.toLowerCase().startsWith(`stage ${stage.stageNumber}`)
                            ? stage.stageName
                            : `Stage ${stage.stageNumber}: ${stage.stageName}`}
                        </span>
                        <span className="text-xs font-mono font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                          {stage.workOrderNumber}
                        </span>
                        <span className="text-xs text-slate-500 font-medium">
                          • {workCenterLookup.labelOf((stage as any).workCenterId ?? (stage as any).work_center_id, stage.workCenterName)}
                          {stage.machineName ? ` (${stage.machineName})` : ''}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 flex-wrap">
                        {stage.isMandatoryGate && totalCount > 0 ? (
                          <>
                            {isStageCompleted ? (
                              <span className="text-emerald-700 font-medium flex items-center gap-1">
                                <i className="ri-checkbox-circle-fill text-emerald-600 text-xs" />
                                {passedCount} of {totalCount} Checkpoints Passed
                              </span>
                            ) : isStageFailed ? (
                              <span className="text-rose-700 font-medium flex items-center gap-1">
                                <i className="ri-close-circle-fill text-rose-600 text-xs" />
                                QC Failed — Rework Needed
                              </span>
                            ) : isStagePendingQc ? (
                              <span className="text-purple-700 font-medium flex items-center gap-1">
                                <i className="ri-microscope-line text-purple-600 text-xs" />
                                Waiting for QC Inspection ({totalCount} Checkpoints)
                              </span>
                            ) : (
                              <span className="text-slate-500 font-medium">
                                {totalCount} Checkpoints Scheduled
                              </span>
                            )}
                            {stage.checklistName && (
                              <span className="text-slate-500">
                                • Checklist: <strong className="text-slate-700 font-semibold">{stage.checklistName}</strong>
                              </span>
                            )}
                            {stage.inspectorName && isStageCompleted && (
                              <span className="text-slate-500">
                                • Inspector: <strong className="text-slate-700 font-semibold">{stage.inspectorName}</strong>
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-slate-400 font-medium flex items-center gap-1.5">
                            <i className="ri-information-line text-xs" />
                            QC Inspection Not Required
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Badges & Actions */}
                  <div className="flex items-center gap-2.5 shrink-0">
                    {stage.isMandatoryGate && totalCount > 0 ? (
                      isStageCompleted ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                          <i className="ri-checkbox-circle-fill text-emerald-600 text-xs" /> PASSED
                        </span>
                      ) : isStageFailed ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                          <i className="ri-close-circle-fill text-rose-600 text-xs" /> QC FAILED
                        </span>
                      ) : isStagePendingQc ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 inline-flex items-center gap-1">
                          <i className="ri-microscope-line text-purple-600 text-xs" /> QC PENDING
                        </span>
                      ) : isStageInProgress ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 inline-flex items-center gap-1">
                          <i className="ri-loader-4-line animate-spin text-blue-600 text-xs" /> IN PROGRESS
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200 inline-flex items-center gap-1">
                          <i className="ri-time-line text-slate-400 text-xs" /> PENDING
                        </span>
                      )
                    ) : isStageCompleted ? (
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                        <i className="ri-checkbox-circle-fill text-emerald-600 text-xs" /> COMPLETED
                      </span>
                    ) : isStageInProgress ? (
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 inline-flex items-center gap-1">
                        <i className="ri-loader-4-line animate-spin text-blue-600 text-xs" /> IN PROGRESS
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200 inline-flex items-center gap-1">
                        <i className="ri-time-line text-slate-400 text-xs" /> PENDING
                      </span>
                    )}

                    {/* Record Inspection Button */}
                    {!isCompletedOrder && order?.status !== 'DRAFT' && order?.status !== 'ON_HOLD' && stage.isMandatoryGate && totalCount > 0 && (isStagePendingQc || isStageInProgress || isStageFailed) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenInspectionModal(stage);
                        }}
                        className="h-8 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs flex items-center gap-1.5 shadow-2xs transition-colors cursor-pointer"
                      >
                        <i className="ri-microscope-line text-xs" />
                        <span>Record Inspection</span>
                      </button>
                    )}

                    {/* Quick Pass Button */}
                    {!isCompletedOrder && order?.status !== 'DRAFT' && order?.status !== 'ON_HOLD' && stage.isMandatoryGate && totalCount > 0 && isStagePendingQc && onQuickPassQc && (
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await onQuickPassQc(matchingWo);
                          fetchQualityData();
                        }}
                        disabled={actionLoading}
                        className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs flex items-center gap-1 shadow-2xs transition-colors cursor-pointer disabled:opacity-50"
                      >
                        <i className="ri-check-line text-xs" />
                        <span>Pass</span>
                      </button>
                    )}

                    {/* Accordion Chevron */}
                    <i
                      className={`ri-arrow-down-s-line text-slate-400 text-lg transition-transform duration-200 ${
                        isExpanded ? 'rotate-180 text-slate-700' : ''
                      }`}
                    />
                  </div>
                </div>

                {/* ── Stage Parameters Table ── */}
                {isExpanded && (
                  <div>
                    {stage.parameters.length === 0 ? (
                      <div className="p-5 text-center bg-slate-50/40 border-t border-slate-100">
                        <div className="inline-flex items-center gap-2 text-xs text-slate-500 font-medium">
                          <i className="ri-information-line text-slate-400 text-base" />
                          <span>No QC inspection checkpoints are configured for this routing stage. Work order progresses directly.</span>
                        </div>
                      </div>
                    ) : (
                      <div className="border-t border-slate-100 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50/70 text-slate-500 font-semibold border-b border-slate-200 text-xs uppercase tracking-wider">
                            <tr>
                              <th className="px-5 py-3.5 text-center w-12">#</th>
                              <th className="px-5 py-3.5 text-left">Parameter Name</th>
                              <th className="px-5 py-3.5 text-left">Specification / Tolerance</th>
                              <th className="px-5 py-3.5 text-left">Observed Value</th>
                              <th className="px-5 py-3.5 text-center">Status</th>
                              <th className="px-5 py-3.5 text-left">Remarks / Notes</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {stage.filteredParameters.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="px-5 py-6 text-center text-xs text-slate-400">
                                  No checkpoints match current filter in this stage.
                                </td>
                              </tr>
                            ) : (
                              stage.filteredParameters.map((param, pIdx) => {
                                const isPassed = param.status === 'PASSED';
                                const isFailed = param.status === 'FAILED';

                                return (
                                  <tr
                                    key={param.id || pIdx}
                                    className={`hover:bg-slate-50/60 transition-colors ${
                                      isFailed ? 'bg-rose-50/20' : ''
                                    }`}
                                  >
                                    {/* Number */}
                                    <td className="px-5 py-4 text-center font-bold text-slate-400 text-xs">
                                      {pIdx + 1}
                                    </td>

                                    {/* Parameter Name */}
                                    <td className="px-5 py-4 font-semibold text-slate-800 text-sm">
                                      <div className="flex items-center gap-1.5">
                                        <span>{param.name}</span>
                                        {param.mandatory && (
                                          <span className="text-rose-500 font-bold" title="Mandatory checkpoint">
                                            *
                                          </span>
                                        )}
                                      </div>
                                    </td>

                                    {/* Specification */}
                                    <td className="px-5 py-4 text-xs font-medium text-slate-600">
                                      <div>{param.specification || 'Pass / Fail'}</div>
                                      {param.tolerance && (
                                        <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                                          Tolerance: {param.tolerance}
                                        </div>
                                      )}
                                    </td>

                                    {/* Observed Value */}
                                    <td className="px-5 py-4">
                                      {param.observedValue ? (
                                        <span
                                          className={`font-mono text-xs font-bold ${
                                            isPassed
                                              ? 'text-emerald-700'
                                              : isFailed
                                              ? 'text-rose-700'
                                              : 'text-slate-800'
                                          }`}
                                        >
                                          {param.observedValue}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-slate-400 italic">
                                          Pending inspection
                                        </span>
                                      )}
                                    </td>

                                    {/* Status Badge */}
                                    <td className="px-5 py-4 text-center">
                                      {isPassed ? (
                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                                          <i className="ri-checkbox-circle-fill text-emerald-600 text-xs" /> PASSED
                                        </span>
                                      ) : isFailed ? (
                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                                          <i className="ri-close-circle-fill text-rose-600 text-xs" /> FAILED
                                        </span>
                                      ) : (
                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200 inline-flex items-center gap-1">
                                          <i className="ri-time-line text-slate-400 text-xs" /> PENDING
                                        </span>
                                      )}
                                    </td>

                                    {/* Remarks */}
                                    <td className="px-5 py-4 text-xs text-slate-500">
                                      {param.remarks || (isPassed ? 'Conforms to specification' : '—')}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Stage Footer Summary */}
                    {stage.isMandatoryGate && stage.parameters.length > 0 && (
                      <div className="px-5 py-3 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <i className="ri-user-follow-line text-slate-400" />
                          <span>
                            Stage Sign-off:{' '}
                            <strong className="text-slate-700 font-semibold">
                              {stage.inspectorName || 'QC Inspector'}
                            </strong>
                            {stage.completedAt ? ` (${stage.completedAt})` : ''}
                          </span>
                        </div>
                        {stage.inspectionNumber && (
                          <span className="text-[11px] font-mono text-slate-400">
                            Inspection #{stage.inspectionNumber}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ── 3. Non-Conformance Reports (NCRs) If Present ── */}
      {backendNcrs.length > 0 && (
        <div className="bg-white rounded-2xl border border-rose-200/90 shadow-xs overflow-hidden">
          <div className="p-4 sm:p-5 bg-white border-b border-rose-100 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <i className="ri-alert-fill text-rose-600 text-lg" />
              <div>
                <h4 className="text-sm font-bold text-slate-800">
                  Active Non-Conformance Reports (NCRs) ({backendNcrs.length})
                </h4>
                <p className="text-xs text-slate-500">
                  Deviations or defects logged during routing stage inspections for this production order
                </p>
              </div>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {backendNcrs.map((ncr, nIdx) => (
              <div key={nIdx} className="p-4 sm:p-5 flex items-start justify-between gap-4 flex-wrap">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-xs text-rose-700 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                      {ncr.ncrNumber || ncr.ncr_number || `NCR-${nIdx + 1}`}
                    </span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                      Severity: {ncr.severity || 'MAJOR'}
                    </span>
                    <span className="text-xs font-semibold text-slate-700">
                      {ncr.itemName || ncr.item_name || productName}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 font-medium">
                    {ncr.defectDescription || ncr.defect_description || 'Out of specification parameter detected during inspection.'}
                  </p>
                  {ncr.dispositionAction && (
                    <p className="text-[11px] text-slate-500">
                      Disposition: <strong className="text-slate-700 font-medium">{ncr.dispositionAction}</strong>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                    {ncr.status || 'OPEN'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 4. Finished Goods Quality Dossier Footer ── */}
      <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 border border-indigo-100">
            <i className="ri-file-shield-line text-xl" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-slate-800">
              Finished Goods Quality Dossier &amp; Traceability
            </h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Production Order: <strong className="text-slate-700 font-semibold">{poNumber}</strong> | Product:{' '}
              <strong className="text-slate-700 font-semibold">{productName}</strong> | Batch:{' '}
              <strong className="text-slate-700 font-semibold">{batchNumber}</strong> | Total Units:{' '}
              <strong className="text-slate-700 font-semibold">{order?.completed_qty || order?.planned_qty || 0} Pcs</strong>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPrintModalOpen(true)}
            className="h-9 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold shadow-2xs flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <i className="ri-printer-line text-sm text-slate-500" />
            <span>Print Quality Card</span>
          </button>
        </div>
      </div>

      {/* ── 5. Interactive Record Inspection Modal (Fallback) ── */}
      {inspectModalStage && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-white">
              <div className="flex items-center gap-2.5">
                <i className="ri-microscope-line text-indigo-600 text-lg" />
                <div>
                  <h3 className="text-base font-bold text-slate-800 tracking-tight">
                    Record Quality Inspection
                  </h3>
                  <p className="text-xs text-slate-500">
                    {inspectModalStage.stageName} ({inspectModalStage.workOrderNumber}) · {productName}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setInspectModalStage(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto text-xs">
              {/* Inspector field */}
              <div className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl border border-slate-200">
                <label className="font-semibold text-slate-700">Inspector Name / Sign-off:</label>
                <input
                  type="text"
                  value={modalInspectorName}
                  onChange={(e) => setModalInspectorName(e.target.value)}
                  placeholder="Enter inspector name..."
                  className="h-8 px-3 border border-slate-200 rounded-lg text-xs font-semibold w-64 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              {/* Dynamic Checkpoints Form */}
              <div className="space-y-3">
                <h5 className="font-bold text-slate-700 uppercase tracking-wider text-[11px]">
                  Checkpoints &amp; Specifications ({inspectModalStage.parameters.length})
                </h5>
                {inspectModalStage.parameters.map((param) => {
                  const currentVal = modalParamForm[param.id] || {
                    observedValue: '',
                    status: 'PASS',
                    remarks: '',
                  };

                  return (
                    <div
                      key={param.id}
                      className="p-3.5 bg-slate-50/70 rounded-xl border border-slate-200 space-y-2.5"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <span className="font-bold text-slate-800 block text-xs">
                            {param.name} {param.mandatory && <span className="text-rose-500">*</span>}
                          </span>
                          <span className="text-[11px] text-slate-500">
                            Specification: <strong>{param.specification}</strong>
                            {param.tolerance ? ` (Tolerance: ${param.tolerance})` : ''}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 border-t border-slate-200/60">
                        <div>
                          <label className="text-[10px] font-bold text-slate-500 block mb-1">
                            Observed Measurement:
                          </label>
                          <input
                            type="text"
                            value={currentVal.observedValue}
                            onChange={(e) =>
                              setModalParamForm((prev) => ({
                                ...prev,
                                [param.id]: {
                                  ...prev[param.id],
                                  observedValue: e.target.value,
                                },
                              }))
                            }
                            placeholder="e.g. 50.02 mm or Verified"
                            className="w-full h-8 px-2.5 bg-white border border-slate-200 rounded-lg text-xs font-mono font-bold focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        </div>

                        <div>
                          <label className="text-[10px] font-bold text-slate-500 block mb-1">
                            Checkpoint Result:
                          </label>
                          <select
                            value={currentVal.status}
                            onChange={(e) =>
                              setModalParamForm((prev) => ({
                                ...prev,
                                [param.id]: {
                                  ...prev[param.id],
                                  status: e.target.value as 'PASS' | 'FAIL',
                                },
                              }))
                            }
                            className={`w-full h-8 px-2.5 rounded-lg text-xs font-bold border transition-colors cursor-pointer ${
                              currentVal.status === 'PASS'
                                ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                                : 'bg-rose-50 text-rose-800 border-rose-300'
                            }`}
                          >
                            <option value="PASS">PASS — WITHIN TOLERANCE</option>
                            <option value="FAIL">FAIL — OUT OF SPECIFICATION</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setInspectModalStage(null)}
                className="h-9 px-4 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50 transition-colors cursor-pointer"
              >
                Cancel
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleSubmitInspection('FAIL')}
                  disabled={modalSubmitting || actionLoading}
                  className="h-9 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold transition-colors shadow-2xs cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                >
                  <i className="ri-close-line text-sm" />
                  <span>Reject &amp; Raise NCR</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleSubmitInspection('PASS')}
                  disabled={modalSubmitting || actionLoading}
                  className="h-9 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors shadow-2xs cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                >
                  <i className="ri-check-line text-sm" />
                  <span>Pass Stage Gate</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 6. Printable Quality Card Modal ── */}
      {printModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Header */}
            <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
              <div className="flex items-center gap-2.5">
                <i className="ri-file-shield-line text-indigo-600 text-lg" />
                <span className="font-bold text-base text-slate-800">Quality Inspection Certificate Preview</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="h-8 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold flex items-center gap-1.5 cursor-pointer shadow-2xs transition-colors"
                >
                  <i className="ri-printer-line text-sm" />
                  <span>Print Document</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPrintModalOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>
            </div>

            {/* Printable Content Body */}
            <div className="p-6 sm:p-8 space-y-6 overflow-y-auto text-slate-800 text-xs bg-white">
              <div className="border-b border-slate-300 pb-4 flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-black text-slate-900 tracking-tight">
                    DIGI INDIA MANUFACTURING &amp; ENGINEERING
                  </h2>
                  <p className="text-slate-500 font-mono text-[11px] mt-0.5">
                    ISO 9001:2015 Certified Manufacturing Facility
                  </p>
                  <p className="text-slate-600 text-xs font-bold mt-2">
                    QUALITY INSPECTION CERTIFICATE &amp; GATE DOSSIER
                  </p>
                </div>
                <div className="text-right">
                  <span className="font-mono font-bold text-xs bg-slate-100 px-2.5 py-1 rounded border border-slate-300 inline-block">
                    {poNumber}
                  </span>
                  <p className="text-[10px] text-slate-400 font-mono mt-1">
                    Date: {new Date().toLocaleDateString('en-IN')}
                  </p>
                </div>
              </div>

              {/* Order Metadata Table */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">Production Order</span>
                  <span className="font-mono font-bold text-slate-800">{poNumber}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">Finished Good</span>
                  <span className="font-bold text-slate-800">{productName}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">Lot / Batch Number</span>
                  <span className="font-mono font-bold text-slate-800">{batchNumber}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase font-bold block">Total Quantity</span>
                  <span className="font-bold text-emerald-700">
                    {order?.completed_qty || order?.planned_qty || 0} Units Passed
                  </span>
                </div>
              </div>

              {/* Stages Summary Table */}
              <div className="space-y-4">
                <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider border-b border-slate-200 pb-1">
                  Routing Stage Quality Sign-offs
                </h4>
                {stageQcList.map((stage) => (
                  <div key={stage.stageNumber} className="border border-slate-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between font-bold text-xs">
                      <span>
                        Stage {stage.stageNumber}: {stage.stageName} ({stage.workOrderNumber})
                      </span>
                      <span className="text-emerald-700 font-mono font-bold">
                        {stage.status === 'COMPLETED' ? '✓ GATE CLEARED' : stage.status}
                      </span>
                    </div>
                    {stage.parameters.length === 0 ? (
                      <p className="text-[11px] text-slate-400 italic py-1 border-t border-slate-100 mt-1">
                        No quality inspection checkpoints required for this stage.
                      </p>
                    ) : (
                      <table className="w-full text-[11px] text-left border-t border-slate-100 mt-1">
                        <thead>
                          <tr className="text-slate-400 uppercase text-[9px]">
                            <th className="py-1">Parameter</th>
                            <th className="py-1">Specification</th>
                            <th className="py-1">Observed Value</th>
                            <th className="py-1 text-right">Result</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {stage.parameters.map((p) => (
                            <tr key={p.id}>
                              <td className="py-1 font-semibold text-slate-700">{p.name}</td>
                              <td className="py-1 text-slate-600">{p.specification}</td>
                              <td className="py-1 font-mono text-slate-800">{p.observedValue || 'Verified'}</td>
                              <td className="py-1 text-right font-bold text-emerald-700">PASS</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>

              {/* Signoff Stamp Box */}
              <div className="pt-6 border-t border-slate-200 grid grid-cols-2 gap-8 text-center text-xs">
                <div>
                  <div className="h-14 border-b border-dashed border-slate-400 mb-1" />
                  <span className="font-bold text-slate-700">Shop Floor Lead / Operator</span>
                  <p className="text-[10px] text-slate-400 mt-0.5">Verification Sign-off</p>
                </div>
                <div>
                  <div className="h-14 border-b border-dashed border-slate-400 mb-1" />
                  <span className="font-bold text-slate-700">Quality Assurance (QA / QC) Manager</span>
                  <p className="text-[10px] text-slate-400 mt-0.5">Final Inspection Clearance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
